/**
 * SoleadLab 2.0 — Photo Registration Service
 *
 * Corrects drone thermal photo positioning on the map. The GPS coordinates in
 * EXIF represent where the DRONE is, but the camera images ground modules that
 * are offset due to gimbal tilt. This service computes and applies that offset.
 *
 * Three tiers:
 *   1. Analytical — per-photo offset from EXIF gimbal pitch/yaw + altitude
 *   2. Manual analytical — user provides heading + tilt, applied uniformly
 *   3. Anchor-based — user places 2-3 photos manually, rigid transform for all
 */

import { centroidOfPolygon } from '../utils/geometry';
import { resolveImageSrc, getPhotoBlobEntry } from '../utils/photoBlobs';
import { extractExifData } from '../utils/exif';
import { solveTransform, transformPoint, lngLatToMercator } from '../domain/layout/transform';
import type { AnchorPair } from '../domain/layout/types';
import type { Indication, GeoPhoto } from '../stores/projectStore';
import { useProjectStore } from '../stores';

// ─── Types ────────────────────────────────────────────────────

export interface FlightGroup {
  id: string;               // capture date string
  captureDate: string;
  photoIds: string[];        // indication + geoPhoto IDs
  count: number;
  gimbalDataCount: number;   // how many have gimbal pitch+yaw
  avgAltitude: number | null;
}

export interface PhotoInput {
  id: string;
  name: string;
  url: string;
  lng: number;
  lat: number;
  altitude: number | null;
  gimbalPitch: number | null;
  gimbalYaw: number | null;
  flightYaw: number | null;
  bearing: number | null;
  isGeoPhoto: boolean;        // true = GeoPhoto, false = Indication
}

export interface GimbalOffset {
  deltaLng: number;
  deltaLat: number;
  offsetMeters: number;
  directionDeg: number;       // compass direction of offset
}

export interface RegistrationAnchor {
  photoId: string;
  originalLng: number;
  originalLat: number;
  correctedLng: number;
  correctedLat: number;
}

export interface RegistrationResult {
  photoId: string;
  photoName: string;
  oldPos: [number, number];  // [lng, lat]
  newPos: [number, number];
  offsetMeters: number;
  method: 'gimbal' | 'manual' | 'anchor';
}

export interface AnchorCandidate {
  photoId: string;
  thumbnailUrl: string;
  lng: number;
  lat: number;
  score: number;
  reason: string;
}

// ─── Flight Grouping ──────────────────────────────────────────

export function groupPhotosByFlight(
  indications: Indication[],
  geoPhotos: GeoPhoto[],
): FlightGroup[] {
  const groups = new Map<string, FlightGroup>();

  for (const ind of indications) {
    if (ind.type !== 'Photo') continue;
    const date = ind.properties.captureDate || 'unknown';
    if (!groups.has(date)) {
      groups.set(date, { id: date, captureDate: date, photoIds: [], count: 0, gimbalDataCount: 0, avgAltitude: null });
    }
    const g = groups.get(date)!;
    g.photoIds.push(ind.id);
    g.count++;
    if (ind.properties.gimbalPitch != null && ind.properties.gimbalYaw != null) {
      g.gimbalDataCount++;
    }
  }

  for (const gp of geoPhotos) {
    const date = gp.captureDate || 'unknown';
    if (!groups.has(date)) {
      groups.set(date, { id: date, captureDate: date, photoIds: [], count: 0, gimbalDataCount: 0, avgAltitude: null });
    }
    const g = groups.get(date)!;
    g.photoIds.push(gp.id);
    g.count++;
  }

  return Array.from(groups.values()).sort((a, b) => b.count - a.count);
}

// ─── Photo Input Extraction ───────────────────────────────────

function indicationToInput(ind: Indication): PhotoInput | null {
  const coords = ind.geometry.type === 'Polygon'
    ? centroidOfPolygon((ind.geometry as GeoJSON.Polygon).coordinates[0])
    : ind.geometry.type === 'Point'
      ? (ind.geometry as GeoJSON.Point).coordinates as [number, number]
      : null;
  if (!coords) return null;

  const p = ind.properties;
  return {
    id: ind.id,
    name: (p.image ?? '').split('/').pop() || ind.id,
    url: resolveImageSrc(p.image ?? ''),
    lng: coords[0],
    lat: coords[1],
    altitude: p.relativeAltitude ?? p.altitude ?? null,
    gimbalPitch: p.gimbalPitch ?? null,
    gimbalYaw: p.gimbalYaw ?? null,
    flightYaw: p.flightYaw ?? null,
    bearing: p.bearing ?? null,
    isGeoPhoto: false,
  };
}

function geoPhotoToInput(gp: GeoPhoto): PhotoInput {
  const exif = gp.exifData ?? {};
  return {
    id: gp.id,
    name: gp.url.split('/').pop() || gp.id,
    url: resolveImageSrc(gp.url),
    lng: gp.coordinates[0],
    lat: gp.coordinates[1],
    altitude: (exif.relativeAltitude as number) ?? (exif.altitude as number) ?? null,
    gimbalPitch: (exif.GimbalPitchDegree as number) ?? (exif.gimbalPitch as number) ?? null,
    gimbalYaw: (exif.GimbalYawDegree as number) ?? (exif.gimbalYaw as number) ?? null,
    flightYaw: (exif.FlightYawDegree as number) ?? (exif.flightYaw as number) ?? null,
    bearing: gp.bearing ?? null,
    isGeoPhoto: true,
  };
}

export function getPhotosForFlight(
  flightGroup: FlightGroup,
  indications: Indication[],
  geoPhotos: GeoPhoto[],
): PhotoInput[] {
  const idSet = new Set(flightGroup.photoIds);
  const inputs: PhotoInput[] = [];

  for (const ind of indications) {
    if (ind.type !== 'Photo' || !idSet.has(ind.id)) continue;
    const input = indicationToInput(ind);
    if (input) inputs.push(input);
  }
  for (const gp of geoPhotos) {
    if (!idSet.has(gp.id)) continue;
    inputs.push(geoPhotoToInput(gp));
  }

  return inputs;
}

// ─── EXIF Re-extraction (for photos missing gimbal data) ──────

export async function enrichPhotosWithExif(photos: PhotoInput[]): Promise<number> {
  let enriched = 0;
  for (const photo of photos) {
    if (photo.gimbalPitch != null && photo.gimbalYaw != null && photo.altitude != null) continue;

    // Try to get the original File from the blob store
    const name = photo.name;
    const blobEntry = getPhotoBlobEntry(`photos/${name}`);
    if (!blobEntry?.file) continue;

    try {
      const exif = await extractExifData(blobEntry.file);
      if (photo.altitude == null) photo.altitude = exif.relativeAltitude ?? exif.altitude ?? null;
      if (photo.gimbalPitch == null) photo.gimbalPitch = exif.gimbalPitch ?? null;
      if (photo.gimbalYaw == null) photo.gimbalYaw = exif.gimbalYaw ?? null;
      if (photo.flightYaw == null) photo.flightYaw = exif.flightYaw ?? null;
      if (photo.bearing == null) photo.bearing = exif.bearing ?? null;
      enriched++;
    } catch { /* continue */ }
  }
  return enriched;
}

// ─── Tier 1: Analytical Gimbal Offset ─────────────────────────

const DEG2RAD = Math.PI / 180;

/**
 * Compute the ground offset for a single photo based on gimbal geometry.
 * The camera images a point that is offset from the drone's GPS position
 * by `altitude × tan(tiltAngle)` meters in the gimbal yaw direction.
 */
export function computeGimbalOffset(
  gimbalPitch: number,  // e.g., -62.8 (degrees, negative = tilted down)
  gimbalYaw: number,    // e.g., -1.1 (degrees clockwise from north)
  altitude: number,     // meters above ground
  lat: number,          // for lng/lat conversion
): GimbalOffset {
  // Tilt from nadir: -90° pitch = 0° tilt (straight down), -62.8° = 27.2° tilt
  const tiltFromNadir = 90 + gimbalPitch; // gimbalPitch is negative

  // If nearly nadir (< 1° tilt), no meaningful offset
  if (Math.abs(tiltFromNadir) < 1) {
    return { deltaLng: 0, deltaLat: 0, offsetMeters: 0, directionDeg: gimbalYaw };
  }

  const offsetMeters = altitude * Math.tan(Math.abs(tiltFromNadir) * DEG2RAD);

  // Offset direction: gimbal yaw (degrees clockwise from north)
  const yawRad = gimbalYaw * DEG2RAD;
  const deltaNorth = offsetMeters * Math.cos(yawRad);
  const deltaEast = offsetMeters * Math.sin(yawRad);

  // Convert meters to degrees
  const deltaLat = deltaNorth / 111320;
  const deltaLng = deltaEast / (111320 * Math.cos(lat * DEG2RAD));

  return { deltaLng, deltaLat, offsetMeters, directionDeg: gimbalYaw };
}

/**
 * Tier 1: Correct all photos using per-photo EXIF gimbal data.
 * Only processes photos that have gimbalPitch + gimbalYaw + altitude.
 */
export function batchAnalyticalCorrection(
  photos: PhotoInput[],
  defaultAltitude: number = 50,
): RegistrationResult[] {
  const results: RegistrationResult[] = [];

  for (const photo of photos) {
    if (photo.gimbalPitch == null || photo.gimbalYaw == null) continue;
    const alt = photo.altitude ?? defaultAltitude;

    const offset = computeGimbalOffset(photo.gimbalPitch, photo.gimbalYaw, alt, photo.lat);

    results.push({
      photoId: photo.id,
      photoName: photo.name,
      oldPos: [photo.lng, photo.lat],
      newPos: [photo.lng + offset.deltaLng, photo.lat + offset.deltaLat],
      offsetMeters: offset.offsetMeters,
      method: 'gimbal',
    });
  }

  return results;
}

// ─── Tier 2: User-Guided Analytical ──────────────────────────

/**
 * Tier 2: Correct all photos using user-provided heading + tilt.
 * Same formula as Tier 1 but with uniform heading/tilt for all photos.
 */
export function batchManualCorrection(
  photos: PhotoInput[],
  headingDeg: number,      // drone heading in degrees (0=north)
  tiltFromNadirDeg: number, // tilt angle from straight-down (e.g., 10 = slight tilt)
  defaultAltitude: number = 50,
): RegistrationResult[] {
  // Convert tilt to gimbal pitch: tilt 10° from nadir → pitch = -80°
  const gimbalPitch = -(90 - tiltFromNadirDeg);

  return photos.map((photo) => {
    const alt = photo.altitude ?? defaultAltitude;
    const offset = computeGimbalOffset(gimbalPitch, headingDeg, alt, photo.lat);
    return {
      photoId: photo.id,
      photoName: photo.name,
      oldPos: [photo.lng, photo.lat],
      newPos: [photo.lng + offset.deltaLng, photo.lat + offset.deltaLat],
      offsetMeters: offset.offsetMeters,
      method: 'manual' as const,
    };
  });
}

// ─── Tier 3: Anchor-Based Rigid Transform ─────────────────────

/**
 * Compute a rigid transform from anchor pairs.
 * Reuses solveTransform() from the layout transform module.
 * Returns null if fewer than 2 anchors.
 */
export function computeAnchorTransform(anchors: RegistrationAnchor[]) {
  if (anchors.length < 2) return null;

  // Convert to AnchorPair format: original GPS as "source" (pdfX/pdfY in Mercator),
  // corrected position as "destination" (mapLng/mapLat in WGS84)
  const pairs: AnchorPair[] = anchors.map((a, i) => {
    const [mx, my] = lngLatToMercator(a.originalLng, a.originalLat);
    return {
      id: `anchor-${i}`,
      pdfX: mx,
      pdfY: my,
      mapLng: a.correctedLng,
      mapLat: a.correctedLat,
    };
  });

  return solveTransform(pairs);
}

/**
 * Tier 3: Apply anchor-based rigid transform to all photos.
 */
export function batchAnchorCorrection(
  photos: PhotoInput[],
  anchors: RegistrationAnchor[],
): RegistrationResult[] {
  const transform = computeAnchorTransform(anchors);
  if (!transform) return [];

  return photos.map((photo) => {
    const [mx, my] = lngLatToMercator(photo.lng, photo.lat);
    const [newLng, newLat] = transformPoint(transform, mx, my);
    const dlat = newLat - photo.lat;
    const dlng = newLng - photo.lng;
    const offsetMeters = Math.sqrt(
      (dlat * 111320) ** 2 +
      (dlng * 111320 * Math.cos(photo.lat * DEG2RAD)) ** 2
    );
    return {
      photoId: photo.id,
      photoName: photo.name,
      oldPos: [photo.lng, photo.lat],
      newPos: [newLng, newLat],
      offsetMeters,
      method: 'anchor' as const,
    };
  });
}

// ─── Apply Results to Store ───────────────────────────────────

export function applyRegistrationResults(results: RegistrationResult[]): void {
  const store = useProjectStore.getState();

  for (const r of results) {
    if (r.newPos[0] === r.oldPos[0] && r.newPos[1] === r.oldPos[1]) continue;
    const deltaLng = r.newPos[0] - r.oldPos[0];
    const deltaLat = r.newPos[1] - r.oldPos[1];

    // Try as Photo indication
    const ind = store.data.indications.find((i) => i.id === r.photoId);
    if (ind) {
      const coords = ind.geometry.type === 'Polygon'
        ? (ind.geometry as GeoJSON.Polygon).coordinates[0]
        : null;
      if (coords) {
        const newCoords = coords.map(([lng, lat]) => [lng + deltaLng, lat + deltaLat]);
        store.updateIndication(r.photoId, {
          geometry: { type: 'Polygon', coordinates: [newCoords] } as GeoJSON.Polygon,
          properties: {
            notes: `${ind.properties.notes || ''}\nRegistered: ${r.offsetMeters.toFixed(1)}m offset (${r.method})`,
          },
        });
      }
      continue;
    }

    // Try as GeoPhoto
    const gp = store.data.geoPhotos.find((g) => g.id === r.photoId);
    if (gp) {
      store.updateGeoPhotoCoords(r.photoId, [r.newPos[0], r.newPos[1]]);
    }
  }
}

// ─── Anchor Candidate Ranking ─────────────────────────────────

export function rankAnchorCandidates(
  photos: PhotoInput[],
  orthoBounds: { minLng: number; maxLng: number; minLat: number; maxLat: number } | null,
): AnchorCandidate[] {
  if (photos.length === 0) return [];

  const candidates: AnchorCandidate[] = photos.map((p) => {
    let score = 0;
    const reasons: string[] = [];

    // Edge proximity: closer to orthophoto boundary = more distinctive
    if (orthoBounds) {
      const dLeft = Math.abs(p.lng - orthoBounds.minLng);
      const dRight = Math.abs(p.lng - orthoBounds.maxLng);
      const dTop = Math.abs(p.lat - orthoBounds.maxLat);
      const dBottom = Math.abs(p.lat - orthoBounds.minLat);
      const minDist = Math.min(dLeft, dRight, dTop, dBottom);
      const lngSpan = orthoBounds.maxLng - orthoBounds.minLng;
      const latSpan = orthoBounds.maxLat - orthoBounds.minLat;
      const maxDist = Math.max(lngSpan, latSpan) / 2;
      const edgeScore = maxDist > 0 ? 1 - (minDist / maxDist) : 0;
      score += edgeScore * 0.4;
      if (edgeScore > 0.7) reasons.push('Near edge');
    }

    // Neighbor sparsity: fewer neighbors = likely at array edge
    const neighbors = photos.filter((q) => {
      if (q.id === p.id) return false;
      const dlat = (q.lat - p.lat) * 111320;
      const dlng = (q.lng - p.lng) * 111320 * Math.cos(p.lat * DEG2RAD);
      return Math.sqrt(dlat * dlat + dlng * dlng) < 20;
    }).length;
    const sparsity = Math.max(0, 1 - neighbors / Math.min(10, photos.length));
    score += sparsity * 0.3;
    if (sparsity > 0.7) reasons.push('Isolated');

    // Has bearing
    if (p.bearing != null) {
      score += 0.2;
      reasons.push('Has bearing');
    }

    // Prefer photos with good position variety (spread out from others)
    score += 0.1; // base

    return {
      photoId: p.id,
      thumbnailUrl: p.url,
      lng: p.lng,
      lat: p.lat,
      score,
      reason: reasons.length > 0 ? reasons.join(', ') : 'General',
    };
  });

  return candidates.sort((a, b) => b.score - a.score);
}

// ─── Heading Detection from Flight Path ───────────────────────

/**
 * Compute average flight heading from consecutive GPS coordinates.
 * Returns heading in degrees (0=north, 90=east).
 */
export function computeFlightHeading(
  coordinates: [number, number, number?][],
): number | null {
  if (coordinates.length < 2) return null;

  // Collect individual segment headings
  const headings: number[] = [];
  for (let i = 1; i < coordinates.length; i++) {
    const [lng1, lat1] = coordinates[i - 1];
    const [lng2, lat2] = coordinates[i];
    const dlng = (lng2 - lng1) * Math.cos(((lat1 + lat2) / 2) * DEG2RAD);
    const dlat = lat2 - lat1;
    const dist = Math.sqrt(dlng * dlng + dlat * dlat);
    if (dist < 1e-8) continue; // skip stationary points

    let heading = Math.atan2(dlng, dlat) * (180 / Math.PI);
    if (heading < 0) heading += 360;
    headings.push(heading);
  }

  if (headings.length === 0) return null;

  // Circular mean to handle wrap-around (e.g., 350° and 10° should average to 0°)
  let sinSum = 0, cosSum = 0;
  for (const h of headings) {
    sinSum += Math.sin(h * DEG2RAD);
    cosSum += Math.cos(h * DEG2RAD);
  }
  let mean = Math.atan2(sinSum / headings.length, cosSum / headings.length) * (180 / Math.PI);
  if (mean < 0) mean += 360;

  return Math.round(mean * 10) / 10;
}

// ─── Stats Helper ─────────────────────────────────────────────

export function computeResultStats(results: RegistrationResult[]) {
  if (results.length === 0) return { count: 0, avgOffset: 0, maxOffset: 0, avgDirection: 0 };

  let totalOffset = 0;
  let maxOffset = 0;
  let sinSum = 0, cosSum = 0;

  for (const r of results) {
    totalOffset += r.offsetMeters;
    if (r.offsetMeters > maxOffset) maxOffset = r.offsetMeters;
    const dlat = r.newPos[1] - r.oldPos[1];
    const dlng = r.newPos[0] - r.oldPos[0];
    const dir = Math.atan2(dlng, dlat);
    sinSum += Math.sin(dir);
    cosSum += Math.cos(dir);
  }

  let avgDir = Math.atan2(sinSum, cosSum) * (180 / Math.PI);
  if (avgDir < 0) avgDir += 360;

  return {
    count: results.length,
    avgOffset: totalOffset / results.length,
    maxOffset,
    avgDirection: Math.round(avgDir),
  };
}

/** Compass label for a heading in degrees */
export function compassLabel(deg: number): string {
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const idx = Math.round(((deg % 360) + 360) % 360 / 22.5) % 16;
  return dirs[idx];
}
